import { useState } from "react";
import { Link, useParams } from "react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import MainLayout from "@/components/layouts";
import { getInitials } from "@/lib/utils";
import { useTeamMemberDetail } from "./team-member-detail.hooks";
import { Edit2, Mail, MoreHorizontal, Phone, Trash } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx";
import DocumentUploadModal from "@/components/shared/upload-document/document-upload-modal.tsx";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import SimpleTable, { ColumnDef } from "@/components/shared/simple-table";
import type { DocumentRecord } from "@shyne/shared/api";
import { DocumentOpenLink } from "@/components/shared/documents/document-open-link";
import { shownDay } from "@/lib/documents";
import { SkeletonContent } from "@/components/shared/skeleton-content";
import { organization_user_status } from "@/models/organization-user.model";
import { editsOf, editTitleOf, roleLabel } from "@/lib/permissions/member-roles";
import StatusBadge from "../teammembers/teammembers.components/StatusBadge";
import EditRoleStatusModal from "./team-member-detail.components/edit-role-status-modal";

export default function TeamMemberDetail() {
  const { memberId } = useParams<{ memberId: string }>();
  const {
    detail,
    update,
    deleteDocument,
    loading,
    uploadModalOpen,
    setUploadDocumentModalOpen,
    handleDocumentUpload,
    docPage,
    docPageSize,
    docTotal,
    setDocPage,
    setDocPageSize,
    documents,
    organizationType,
    administers,
    error,
  } = useTeamMemberDetail(memberId);
  const [editOpen, setEditOpen] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);

  if (error && !loading) {
    return (
      <MainLayout
        breadcrumb={[
          { name: "Settings", href: "#" },
          { name: "Team Members", href: "/settings/team-members" },
        ]}
      >
        <p role="alert" className="text-sm text-muted-foreground">
          {error}
        </p>
      </MainLayout>
    );
  }

  if (loading || !detail) {
    return (
      <MainLayout
        breadcrumb={[
          { name: "Settings", href: "#" },
          { name: "Team Members", href: "/settings/team-members" },
          { name: "Loading…", href: "#" },
        ]}
      >
        <SkeletonContent />
      </MainLayout>
    );
  }

  const { member, properties: apartments } = detail;
  const status = (member.details?.status ?? organization_user_status.active) as organization_user_status;
  const editable = administers && editTitleOf(editsOf(organizationType, member.details?.status)) !== null;

  const breadcrumb = [
    { name: "Settings", href: "#" },
    { name: "Team Members", href: "/settings/team-members" },
    { name: member.name, href: "#" },
  ];

  const documentActions: ColumnDef<DocumentRecord>[] = [
    {
      header: "Actions",
      accessor: (d) => (
        <Dialog open={deletingDocId === d.id} onOpenChange={(open) => setDeletingDocId(open ? d.id : null)}>
          <DialogTrigger asChild>
            <Button variant="destructive" size="icon" aria-label={`Delete ${d.filename}`}>
              <Trash className="w-4 h-4" />
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Remove Document?</DialogTitle>
            </DialogHeader>
            <p className="pt-2 text-sm text-muted-foreground">
              Are you sure you want to delete “{d.filename}”? This action cannot be undone.
            </p>
            <DialogFooter className="pt-4">
              <Button variant="outline" onClick={() => setDeletingDocId(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={async () => {
                  const deleted = await deleteDocument(d.id).then(
                    () => true,
                    () => false
                  );
                  setDeletingDocId(null);
                  if (deleted) toast.success("Document deleted successfully.");
                  else toast.error("The document could not be deleted.");
                }}
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ),
      className: "whitespace-nowrap text-right",
    },
  ];

  const docColumns: ColumnDef<DocumentRecord>[] = [
    {
      header: "Document",
      accessor: (d) => <DocumentOpenLink document={d} />,
      cellClassName: "hover:bg-purple-50 dark:hover:bg-purple-900",
    },
    {
      header: "Uploaded",
      accessor: (d) => new Date(d.uploadedAt).toLocaleDateString(),
    },
    {
      header: "Expires",
      accessor: (d) => shownDay(d.expiresOn),
    },
  ];

  return (
    <MainLayout breadcrumb={breadcrumb}>
      <div className="container mx-auto">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <Card className="xl:col-span-3 border-none">
            <CardContent>
              <div className="flex items-center space-y-4 gap-4 ">
                <Avatar className="w-20 h-20">
                  <AvatarImage src={member.details?.avatar ?? ""} />
                  <AvatarFallback>{getInitials(member.name)}</AvatarFallback>
                </Avatar>
                <div className="w-full">
                  <div className="flex justify-start items-center w-full gap-8">
                    <h2 className="text-lg font-semibold">{member.name} </h2>
                    <StatusBadge status={status} />
                  </div>
                  <p className="text-sm text-muted-foreground">{roleLabel(member.role, organizationType)}</p>
                  <p className="text-sm text-muted-foreground">{member.email}</p>
                  <p className="text-sm text-muted-foreground">{member.phone}</p>
                </div>
              </div>
              <div className="flex flex-col items-start text-center space-y-4">
                <p className="text-sm text-muted-foreground text-center">{member.details?.description}</p>
                <div className="grid grid-cols-3 gap-3">
                  <Button variant="outline" size="sm" asChild>
                    <a href={`tel:${member.phone}`}>
                      <Phone className="w-4 h-4 mr-1" /> Call
                    </a>
                  </Button>
                  <Button variant="outline" size="sm" asChild>
                    <a href={`mailto:${member.email}`}>
                      <Mail className="w-4 h-4 mr-1" /> Email
                    </a>
                  </Button>
                  {editable && (
                    <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                      <Edit2 className="w-4 h-4 mr-1" />
                      Edit User
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {administers && (
            <Card className="xl:col-span-3">
              <CardHeader>
                <CardTitle>
                  <div className="flex items-center">
                    <p className="mr-auto uppercase">Documents</p>
                    <Button onClick={() => setUploadDocumentModalOpen(true)}>Upload Document</Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {documents.length > 0 ? (
                  <SimpleTable<DocumentRecord>
                    columns={[...docColumns, ...documentActions]}
                    data={documents}
                    totalCount={docTotal}
                    page={docPage}
                    pageSize={docPageSize}
                    setPage={setDocPage}
                    setPageSize={setDocPageSize}
                    sortKey={undefined}
                    sortOrder="asc"
                    onSortChange={() => {}}
                    filters={[]}
                    onReset={() => {}}
                    onApply={() => {}}
                  />
                ) : (
                  <p className="text-center text-muted-foreground py-6">No documents.</p>
                )}
              </CardContent>
            </Card>
          )}

          {member.role === "propertyManager" && (
            <Card className="xl:col-span-3" role="region" aria-label="Apartments Managed">
              <CardHeader>
                <CardTitle className="uppercase">Apartments Managed</CardTitle>
              </CardHeader>
              <CardContent>
                {apartments.length > 0 ? (
                  <div className="grid grid-cols-1 gap-4">
                    {apartments.map((apt, i) => (
                      <Card key={i} className="p-4 flex justify-between items-start">
                        <div className="flex items-center justify-between gap-4 w-full">
                          <div className="flex items-start gap-3">
                            <Avatar className="w-10 h-10 rounded-none">
                              <AvatarImage src="/vendor.jpg" alt="Vendor" />
                              <AvatarFallback className="rounded-sm">{getInitials(apt.name)}</AvatarFallback>
                            </Avatar>
                            <div>
                              <Link to={`/properties/${apt.id}`} className="block text-lg font-medium hover:underline">
                                <h4 className="font-medium">{apt.name}</h4>
                              </Link>
                              <p className="text-sm text-muted-foreground">{apt.address}</p>
                            </div>
                          </div>

                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button aria-label="Open menu" className="text-muted-foreground hover:text-primary">
                                <MoreHorizontal className="w-5 h-5" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-40">
                              <DropdownMenuItem>
                                <Link to={`/properties/${apt.id}`}>View Details</Link>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4">
                    <p className="text-center text-muted-foreground py-6">No apartments.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <DocumentUploadModal
            open={uploadModalOpen}
            onOpenChange={setUploadDocumentModalOpen}
            onUpload={handleDocumentUpload}
          />
          <EditRoleStatusModal
            open={editOpen}
            onOpenChange={setEditOpen}
            organizationType={organizationType}
            currentRole={member.role}
            currentStatus={member.details?.status}
            onSubmit={async (role, status) => {
              await update(role, status);
              setEditOpen(false);
            }}
          />
        </div>
      </div>
    </MainLayout>
  );
}
